import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  useActiveIntegrationClientsQuery,
  useDeleteIntegrationConnectionMutation,
  useIntegrationConnectionsForOrganizationQuery,
} from '@client/graphql/__generated__/main-operations';
import { X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useAppContext } from '~/services/main/contexts/AppContext';

type IntegrationConnection = NonNullable<
  NonNullable<
    ReturnType<typeof useIntegrationConnectionsForOrganizationQuery>['data']
  >['integrationConnectionsForOrganization']
>[number];

export const OrganizationIntegrationConnections = () => {
  const { selectedOrganization } = useAppContext();
  const { data: activeIntegrationClients, loading: loadingClients } =
    useActiveIntegrationClientsQuery();
  const {
    data: integrationConnections,
    loading: loadingConnections,
    refetch,
  } = useIntegrationConnectionsForOrganizationQuery({
    variables: {
      organizationId: selectedOrganization?.id ?? '',
    },
    skip: !selectedOrganization?.id,
  });

  const [deleteIntegrationConnection, { loading: isDeleting }] =
    useDeleteIntegrationConnectionMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [connectionToDelete, setConnectionToDelete] =
    useState<IntegrationConnection | null>(null);

  const handleDeleteClick = (connection: IntegrationConnection) => {
    setConnectionToDelete(connection);
    onOpen();
  };

  const handleConfirmDelete = () => {
    if (!connectionToDelete) return;

    void deleteIntegrationConnection({
      variables: {
        input: {
          integrationConnectionId: connectionToDelete.id,
        },
      },
    }).then(() => {
      onClose();
      setConnectionToDelete(null);

      return refetch();
    });
  };

  const connectionsByClient = useMemo(() => {
    const map = new Map<string, IntegrationConnection[]>();

    integrationConnections?.integrationConnectionsForOrganization?.forEach(
      (conn) => {
        if (conn.integrationClient?.id) {
          const existing = map.get(conn.integrationClient.id) ?? [];

          map.set(conn.integrationClient.id, [...existing, conn]);
        }
      }
    );

    return map;
  }, [integrationConnections]);

  const { connectedClients, unconnectedClients } = useMemo(() => {
    const connected =
      activeIntegrationClients?.activeIntegrationClients?.filter((client) =>
        connectionsByClient.has(client.id)
      ) ?? [];
    const unconnected =
      activeIntegrationClients?.activeIntegrationClients?.filter(
        (client) => !connectionsByClient.has(client.id)
      ) ?? [];

    return { connectedClients: connected, unconnectedClients: unconnected };
  }, [activeIntegrationClients, connectionsByClient]);

  if (loadingClients || loadingConnections) {
    return <CenterSpinner />;
  }

  return (
    <VStack align="stretch" pt={4} spacing={8}>
      {connectedClients.length > 0 && (
        <Box>
          <Heading mb={4} size="xs">
            Connected Integrations
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {connectedClients.map((client) => {
              const connections = connectionsByClient.get(client.id) ?? [];
              const connectLink = `/integrations/${client.provider}/${client.clientType}/organization-connect?next=${window.location.pathname}`;

              return (
                <Card key={client.id}>
                  <CardBody>
                    <VStack spacing={3}>
                      {client.logoFile?.url && (
                        <Image
                          alt={`${client.provider} logo`}
                          h="60px"
                          objectFit="contain"
                          src={client.logoFile.url}
                        />
                      )}
                      <VStack align="stretch" spacing={1} width="full">
                        {connections.map((conn) => (
                          <React.Fragment key={conn.id}>
                            <HStack alignItems="flex-start" p={2}>
                              <FormControl>
                                <FormLabel mb={0}>Account</FormLabel>
                                <Text fontSize="sm" fontWeight="medium">
                                  {conn.providerAccountName}
                                </Text>
                              </FormControl>
                              <FormControl>
                                <FormLabel mb={0}>Connected By</FormLabel>
                                <Text fontSize="sm" fontWeight="medium">
                                  {conn.user?.fullName}
                                </Text>
                              </FormControl>
                              <Tooltip
                                label="Disconnect"
                                placement="right"
                                variant="muted"
                              >
                                <IconButton
                                  _hover={{
                                    bg: 'whiteAlpha.50',
                                  }}
                                  aria-label="Disconnect"
                                  color="red.500"
                                  icon={<X size={16} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteClick(conn)}
                                />
                              </Tooltip>
                            </HStack>
                            <Divider />
                          </React.Fragment>
                        ))}
                      </VStack>
                    </VStack>
                  </CardBody>
                  <CardFooter>
                    <Link color="primary.600" href={connectLink} width="full">
                      Add Another Connection
                    </Link>
                  </CardFooter>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      {unconnectedClients.length > 0 && (
        <Box>
          <Heading mb={4} size="xs">
            Available Integrations
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {unconnectedClients.map((client) => {
              const connectLink = `/integrations/${client.provider}/${client.clientType}/organization-connect?next=${window.location.pathname}`;

              return (
                <Card key={client.id}>
                  <CardBody>
                    <VStack spacing={2}>
                      {client.logoFile?.url && (
                        <Image
                          alt={`${client.provider} logo`}
                          h="60px"
                          objectFit="contain"
                          src={client.logoFile.url}
                        />
                      )}
                    </VStack>
                  </CardBody>
                  <CardFooter>
                    <Button as={Link} href={connectLink} width="full">
                      Connect
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Disconnect Integration</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to disconnect the integration account{' '}
              <strong>{connectionToDelete?.providerAccountName}</strong>?
            </Text>
            <Text mt={2}>
              This will remove access to this account and may affect any ongoing
              syncs or data connections.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              _hover={{
                bg: 'red.800',
              }}
              bg="red.600"
              color="white"
              colorScheme="red"
              isLoading={isDeleting}
              variant="solid"
              onClick={handleConfirmDelete}
            >
              Disconnect
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};
